import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

const ACTION_COLORS = {
  learner_view:   "bg-slate-100 text-slate-700",
  learner_delete: "bg-rose-100 text-rose-700",
  user_register:  "bg-emerald-100 text-emerald-700",
  login:          "bg-blue-100 text-blue-700",
};

function Row({ e }) {
  const cls = ACTION_COLORS[e.action] || "bg-slate-100 text-slate-700";
  return (
    <tr className="border-b border-slate-100 last:border-0" data-testid={`audit-row-${e.id}`}>
      <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">
        {new Date(e.created_at).toLocaleString()}
      </td>
      <td className="py-2 pr-4">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>
          {e.action.replace(/_/g, " ")}
        </span>
      </td>
      <td className="py-2 pr-4 text-sm text-slate-800 whitespace-nowrap">{e.user_email}</td>
      <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">{e.resource_type || "—"}</td>
      <td className="py-2 pr-4 text-xs text-slate-400 truncate max-w-[240px]">{e.resource_id || ""}</td>
    </tr>
  );
}

export default function AuditPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/audit").then((r) => setEvents(r.data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Owners only");
      nav("/compliance");
    }).finally(() => setLoading(false));
  }, [nav]);

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-6xl">
      <button onClick={() => nav("/compliance")}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Compliance
      </button>
      <PageHeader
        eyebrow="§164.312(b)"
        title="Audit log"
        description={`Append-only record of PHI-touching actions for team ${user?.team_id?.slice(0, 8) || ""}.`}
      />

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : events.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-14 text-center bg-white">
          <ShieldCheck className="w-10 h-10 mx-auto text-slate-400" />
          <div className="mt-3 font-display font-bold text-lg">No events yet</div>
          <p className="text-slate-500 text-sm mt-1">Learner views, deletes, exports, and logins will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 px-4">When</th>
                <th className="py-3 px-4">Action</th>
                <th className="py-3 px-4">Who</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Resource</th>
              </tr>
            </thead>
            <tbody className="[&_td:first-child]:pl-4">
              {events.map((e) => <Row key={e.id} e={e} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
